import { useState, useEffect, useCallback } from 'react';
import { loadCoverLetterData, saveCoverLetterData, defaultCoverLetterData } from '../services/coverLetterStorage';

export const useCoverLetter = () => {
    const [coverData, setCoverData] = useState(defaultCoverLetterData);
    const [loading, setLoading] = useState(true);

    // Load saved data on mount
    useEffect(() => {
        const savedData = loadCoverLetterData();
        setCoverData(savedData);
        setLoading(false);
    }, []);

    // Save data whenever it changes
    useEffect(() => {
        if (!loading) {
            saveCoverLetterData(coverData);
        }
    }, [coverData, loading]);

    // Update personal info
    const updatePersonal = useCallback((field, value) => {
        setCoverData(prev => ({
            ...prev,
            personal: { ...prev.personal, [field]: value }
        }));
    }, []);

    // Update recipient info
    const updateRecipient = useCallback((field, value) => {
        setCoverData(prev => ({
            ...prev,
            recipient: { ...prev.recipient, [field]: value }
        }));
    }, []);

    // Update job details
    const updateJobDetails = useCallback((field, value) => {
        setCoverData(prev => ({
            ...prev,
            jobDetails: { ...prev.jobDetails, [field]: value }
        }));
    }, []);

    // Update content sections
    const updateOpening = useCallback((value) => {
        setCoverData(prev => ({
            ...prev,
            content: { ...prev.content, opening: value }
        }));
    }, []);

    const updateBody = useCallback((value) => {
        setCoverData(prev => ({
            ...prev,
            content: { ...prev.content, body: value }
        }));
    }, []);

    const updateClosing = useCallback((value) => {
        setCoverData(prev => ({
            ...prev,
            content: { ...prev.content, closing: value }
        }));
    }, []);

    // Update additional info
    const updateAdditional = useCallback((field, value) => {
        setCoverData(prev => ({
            ...prev,
            additional: { ...prev.additional, [field]: value }
        }));
    }, []);

    // Reset to defaults
    const resetToDefaults = useCallback(() => {
        setCoverData(defaultCoverLetterData);
    }, []);

    // Load example template
    const loadExample = useCallback((templateType) => {
        if (templateType === 'standard') {
            setCoverData({
                ...defaultCoverLetterData,
                content: {
                    opening: `I am writing to express my strong interest in the position at your company. As a recent graduate with a passion for this field, I am excited about the opportunity to contribute to your team.`,
                    body: `During my studies, I developed strong skills in problem-solving, teamwork, and project management. My academic background has equipped me with the knowledge and dedication needed to succeed in this role.`,
                    closing: `Thank you for considering my application. I look forward to the opportunity to discuss how my skills can benefit your team.`
                }
            });
        } else if (templateType === 'creative') {
            setCoverData({
                ...defaultCoverLetterData,
                jobDetails: { ...defaultCoverLetterData.jobDetails, jobTitle: 'Creative Developer' },
                recipient: { ...defaultCoverLetterData.recipient, companyName: 'Creative Agency' },
                content: {
                    opening: `Creativity meets passion! I'm thrilled to apply for the Creative Developer position. As someone who thinks outside the box, I bring unique solutions and fresh ideas to every challenge.`,
                    body: `My creative portfolio includes innovative campaigns, design thinking, and digital storytelling. I turn ideas into impactful results that drive engagement and growth.`,
                    closing: `Your company's creative culture inspires me. I want to push boundaries, collaborate with talented minds, and create something extraordinary together.`
                }
            });
        }
    }, []);

    return {
        coverData,
        loading,
        updatePersonal,
        updateRecipient,
        updateJobDetails,
        updateOpening,
        updateBody,
        updateClosing,
        updateAdditional,
        resetToDefaults,
        loadExample
    };
};